import type { EventUpdateRequest, SafeAny } from '@/common/types';
import {
  axiosInstance,
  addToQueue,
  purgeOnly,
  setRequestUpdateRequired,
  isRequestUpdateRequired
} from '@/common/utils';
import { ScoreSetEvent } from '../../types';
import { findSignalByEventType, getClonedEventIDbySignal } from '@/core/helpers';

export enum NotificationTypes {
  Success = 'success',
  Warning = 'warning',
  Error = 'danger'
}

const removeEventFromSignal = (commit: SafeAny, eventId: string, eventType: string) => {
  commit('removeEventInSignalEvents', {
    id: eventId,
    type: eventType
  });
};

const revertEventsAndNotifyError = (
  commit: SafeAny,
  events: SafeAny[],
  i18nDescription: string
) => {
  events.forEach((event: SafeAny) => commit('revertRemoveEventInSignalEvents', event));
  commit(
    'addNotification',
    {
      i18nTitle: 'errorDeleteScoreSetEvent',
      i18nDescription,
      type: NotificationTypes.Error
    },
    { root: true }
  );
};

export const deleteStudyScoreSetEvent = async (
  { state, rootState, commit, dispatch }: SafeAny,
  {
    studyId,
    eventId,
    epochSize = 30
  }: {
    studyId: string;
    eventId: string;
    epochSize?: number;
  }
) => {
  const mappedId = state.eventIdMap?.[eventId] ?? eventId;
  let url = `${rootState.env?.requestUrl.events}/studies/${studyId}/events/${eventId}`;

  // current event to allow reverting if needed
  const currentEvent: SafeAny = Object.values(state.signalEvents)
    .flat()
    .find((event: SafeAny) => event.id === eventId || event.id === mappedId);

  const originalEventData = {
    type: currentEvent.eventType,
    start: currentEvent.start,
    duration: currentEvent.duration
  };

  if (!currentEvent) {
    commit(
      'addNotification',
      {
        i18nTitle: 'errorDeleteScoreSetEvent',
        i18nDescription: 'eventNotFound',
        type: NotificationTypes.Error
      },
      { root: true }
    );
    state.eventActions.isLoading = false;
    return;
  }

  removeEventFromSignal(commit, eventId, currentEvent.eventType);

  const wasInsertPurged = purgeOnly(`event-insert:${studyId}:${eventId}`);

  if (wasInsertPurged) {
    state.eventActions.isLoading = false;

    return;
  }

  const executeRequest = async (originalEvent?: ScoreSetEvent) => {
    try {
      const latestMappedId = state.eventIdMap?.[eventId] ?? eventId;
      let finalEventID = latestMappedId;
      if (isRequestUpdateRequired && originalEvent) {
        const signalName = findSignalByEventType(currentEvent.eventType, state.signalEventMapping);
        const clonedEvent = getClonedEventIDbySignal(originalEvent, state, signalName || '');

        if (clonedEvent) {
          finalEventID = state.eventIdMap?.[clonedEvent.id] ?? clonedEvent.id;
          removeEventFromSignal(commit, clonedEvent.id, currentEvent.eventType);
        }
      }

      const isTemporaryId = String(finalEventID).startsWith('temp_');

      if (isTemporaryId) return;

      const resolvedUrl = url.replace(/\/events\/[^/]+$/, `/events/${finalEventID}`);
      const res = await axiosInstance.delete(resolvedUrl);

      if (res?.status === 205) {
        setRequestUpdateRequired(true);
        await dispatch('scoreSet/getScoreSetByStudy', { sleepStudyID: studyId }, { root: true });
        await dispatch('epoch/getStudyScoreSetEvents', { studyId, epochSize }, { root: true });
      } else if (res?.status !== 200) {
        // revert to previous state if response is not 200
        commit('revertRemoveEventInSignalEvents', currentEvent);
        commit(
          'addNotification',
          {
            i18nTitle: 'errorDeleteScoreSetEvent',
            i18nDescription: `Unexpected status code: ${res.status}`,
            type: NotificationTypes.Error
          },
          { root: true }
        );
      }
    } catch (error: SafeAny) {
      // revert to previous state in case of error
      commit('revertRemoveEventInSignalEvents', currentEvent);

      commit(
        'addNotification',
        {
          i18nTitle: 'errorDeleteScoreSetEvent',
          i18nDescription: error.message,
          type: NotificationTypes.Error
        },
        { root: true }
      );
    } finally {
      state.eventActions.isLoading = false;
    }
  };

  purgeOnly(`event-insert:${studyId}:${eventId}`);

  // Add the request to the queue to manage rate-limiting
  addToQueue(
    executeRequest,
    {
      url,
      originalEvent: originalEventData
    } as EventUpdateRequest,
    () => {
      commit(
        'addNotification',
        {
          i18nTitle: 'warningEventLimit',
          i18nDescription: 'warningEventLimitDescription',
          type: NotificationTypes.Warning
        },
        { root: true }
      );
    },
    {
      key: `event:${studyId}:${eventId}`,
      strategy: 'purge-then-append'
    }
  );
};

export const deleteStudyScoreSetAllEventsOfType = async (
  { state, rootState, commit, dispatch }: SafeAny,
  {
    studyId,
    eventType,
    epochSize = 30
  }: {
    studyId: string;
    eventType: string;
    epochSize?: number;
  }
) => {
  const url = `${
    rootState.env?.requestUrl.events
  }/studies/${studyId}/events-type/${encodeURIComponent(eventType)}`;

  const signalName = findSignalByEventType(eventType, state.signalEventMapping);

  const removedEvents: SafeAny[] = (state.signalEvents[signalName || ''] ?? [])
    .filter((event: SafeAny) => event.eventType === eventType)
    .map((event: SafeAny) => ({ ...event }));

  removedEvents.forEach((event: SafeAny) => removeEventFromSignal(commit, event.id, eventType));

  const executeRequest = async () => {
    try {
      const res = await axiosInstance.delete(url);

      if (res?.status === 205) {
        setRequestUpdateRequired(true);
        await dispatch('scoreSet/getScoreSetByStudy', { sleepStudyID: studyId }, { root: true });
        await dispatch('epoch/getStudyScoreSetEvents', { studyId, epochSize }, { root: true });
      } else if (res?.status !== 200) {
        revertEventsAndNotifyError(commit, removedEvents, `Unexpected status code: ${res.status}`);
      }
    } catch (error: SafeAny) {
      revertEventsAndNotifyError(commit, removedEvents, error.message);
    } finally {
      state.eventActions.isLoading = false;
    }
  };

  removedEvents.forEach((event: SafeAny) => {
    purgeOnly(`event-insert:${studyId}:${event.id}`);
    purgeOnly(`event:${studyId}:${event.id}`);
  });

  addToQueue(
    executeRequest,
    { url } as EventUpdateRequest,
    () => {
      commit(
        'addNotification',
        {
          i18nTitle: 'warningEventLimit',
          i18nDescription: 'warningEventLimitDescription',
          type: NotificationTypes.Warning
        },
        { root: true }
      );
    },
    {
      key: `events-of-type:${studyId}:${eventType}`,
      strategy: 'purge-then-append'
    }
  );
};
